// VisionWatcher — watches the vision board for dreams that just came true and
// celebrates them anywhere in the app: the item bursts to full colour and a
// mentor names the lesson. Mounted globally so achievements during normal play
// (a settle, an investment, a mindset gain) are caught wherever they happen.

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { VisionItem } from '@biz-kid/engine';
import { useLife } from './store.js';
import { Button } from '../ui/kit.js';
import { useModalSlot } from '../ui/modalQueue.js';

const MENTOR_LINE = 'You put it on your board. You believed it. And you did the work. That is how it works.';

export function VisionWatcher() {
  const board = useLife().visionBoard;
  const seen = useRef<Set<string> | null>(null);
  const [queue, setQueue] = useState<VisionItem[]>([]);

  useEffect(() => {
    const achievedNow = board.filter((v) => v.achieved);
    if (seen.current === null) {
      // First pass: remember what's already done, don't celebrate retroactively.
      seen.current = new Set(achievedNow.map((v) => v.id));
      return;
    }
    const fresh = achievedNow.filter((v) => !seen.current!.has(v.id));
    if (fresh.length) {
      fresh.forEach((v) => seen.current!.add(v.id));
      setQueue((q) => [...q, ...fresh]);
    }
  }, [board]);

  const current = queue[0];
  // Priority 90 — dream achievements are precious, beat everything except year flow.
  const { canShow } = useModalSlot('vision-celebration', !!current, 90);
  if (!current || !canShow) return null;

  return (
    <AnimatePresence>
      <motion.div
        key={current.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 80,
          display: 'grid',
          placeItems: 'center',
          padding: 20,
          background: 'rgba(10,14,26,0.66)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
          {/* burst */}
          <div style={{ position: 'relative', height: 150, display: 'grid', placeItems: 'center', marginBottom: 8 }}>
            {Array.from({ length: 14 }).map((_, i) => {
              const ang = (i / 14) * Math.PI * 2;
              return (
                <motion.div
                  key={i}
                  initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
                  animate={{ x: Math.cos(ang) * 120, y: Math.sin(ang) * 110, opacity: [0, 1, 0], scale: 1 }}
                  transition={{ duration: 1.1, delay: 0.1 + (i % 4) * 0.04, ease: 'easeOut' }}
                  style={{ position: 'absolute', fontSize: 18 }}
                >
                  {['✨', '🎉', '⭐', '💫'][i % 4]}
                </motion.div>
              );
            })}
            <motion.div
              initial={{ scale: 0.3, rotate: -12, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 12, delay: 0.05 }}
              style={{ fontSize: 84, filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.4))' }}
            >
              {current.icon}
            </motion.div>
          </div>

          <motion.div initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#34d399' }}>
              Dream achieved
            </div>
            <h2 style={{ margin: '4px 0 0', fontSize: 26, fontWeight: 900, color: '#fff' }}>{current.title}</h2>

            {/* mentor */}
            <div
              style={{
                margin: '18px auto 0',
                maxWidth: 360,
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                textAlign: 'left',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.16)',
                borderRadius: 18,
                padding: 14,
              }}
            >
              <span style={{ fontSize: 30, flexShrink: 0 }}>🧑‍🏫</span>
              <span style={{ fontSize: 15, lineHeight: 1.5, color: '#eef2fb', fontWeight: 600 }}>“{MENTOR_LINE}”</span>
            </div>

            <div style={{ marginTop: 18 }}>
              <Button tone="up" size="lg" onClick={() => setQueue((q) => q.slice(1))}>
                Keep going →
              </Button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
